import { useEffect, useState, useRef } from 'react';
import { useDeal } from '../../../hooks/useDeal';
import { useDealsStore } from '../../../store/deals';
import { renderInlineMd, nowIso } from '../../../lib/utils';
import { regenerateSection } from '../../../services/claude';
import { packageApi } from '../../../services/api';
import type { PackageSection, PackageDraft } from '../../../types';

interface Props { dealId: string; }

export function PackageTab({ dealId }: Props) {
  const { deal, data } = useDeal(dealId);
  const savePackage = useDealsStore(s => s.savePackage);
  const appendActivity = useDealsStore(s => s.appendActivity);
  const [tweakingId, setTweakingId] = useState<string | null>(null);

  const [generating, setGenerating] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [pdfFresh, setPdfFresh] = useState(false);

  const [mapHasImg, setMapHasImg] = useState(false);
  const [mapBust, setMapBust] = useState(0);
  const [mapUploading, setMapUploading] = useState(false);
  const mapInputRef = useRef<HTMLInputElement>(null);

  if (!deal) return null;

  const packageDraft = data.packageDraft;

  // Track whether a map exists for this deal
  useEffect(() => {
    setMapHasImg(!!packageDraft?.mapBlobKey);
  }, [packageDraft?.mapBlobKey]);





  const onTweak = async (section: PackageSection, instruction: string) => {
    if (!packageDraft) return;
    setTweakingId(section.id);
    try {
      const result = await regenerateSection({
        deal,
        files: data.files,
        findings: data.findings,
        chain: data.chain,
        picture: data.picture,
        tracts: data.tracts,
        packageDraft,
        recentLeadEvents: data.leadEvents.slice(0, 8),
        conversation: [],
      }, section.name, instruction);

      const updated: PackageSection = {
        ...section,
        bodyMd: result.text,
        status: 'editing',
        lastEditedAt: nowIso(),
      };
      const newDraft: PackageDraft = {
        ...packageDraft,
        version: packageDraft.version + 1,
        sections: packageDraft.sections.map(s => s.id === section.id ? updated : s),
        generatedAt: nowIso(),
        // Tweaking invalidates the rendered PDF
        pdfBlobKey: undefined,
        pdfRenderedAt: undefined,
      };
      await savePackage(newDraft);
      setPdfFresh(false);
    } finally {
      setTweakingId(null);
    }
  };

  const onGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const result = await packageApi.generate(dealId);
      const pkg: PackageDraft = {
        dealId,
        version: result.package.version,
        template: result.package.template,
        sections: result.package.sections,
        generatedAt: result.package.generatedAt,
        mapBlobKey: result.package.mapBlobKey,
      };
      await savePackage(pkg);
      if (result.activity) await appendActivity(result.activity);
      setPdfFresh(false);
    } catch (e) {
      setGenError((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  const onRenderPdf = async () => {
    if (!packageDraft) return;
    setRendering(true);
    setGenError(null);
    try {
      const result = await packageApi.render(dealId);
      // Update the local package draft to reflect the new PDF
      await savePackage({
        ...packageDraft,
        pdfBlobKey: result.pdfBlobKey,
        pdfRenderedAt: result.pdfRenderedAt,
      });
      setPdfFresh(true);
      // Open in new tab
      window.open(packageApi.pdfUrl(dealId) + `&v=${Date.now()}`, '_blank');
    } catch (e) {
      setGenError((e as Error).message);
    } finally {
      setRendering(false);
    }
  };

  const onUploadMap = async (incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const file = Array.from(incoming)[0];
    if (!file) return;
    setMapUploading(true);
    try {
      await packageApi.uploadMap(dealId, file);
      setMapHasImg(true);
      setMapBust(b => b + 1);
      // Update local package draft to reflect new mapBlobKey
      if (packageDraft) {
        await savePackage({ ...packageDraft, mapBlobKey: 'present' });
      }
    } catch (e) {
      alert(`Map upload failed: ${(e as Error).message}`);
    } finally {
      setMapUploading(false);
    }
  };

  const onRemoveMap = async () => {
    if (!window.confirm('Remove the map from this package?')) return;
    try {
      await packageApi.removeMap(dealId);
      setMapHasImg(false);
      if (packageDraft) {
        await savePackage({ ...packageDraft, mapBlobKey: undefined });
      }
    } catch (e) {
      alert(`Remove failed: ${(e as Error).message}`);
    }
  };

  const pdfReady = !!packageDraft?.pdfBlobKey && !!packageDraft?.pdfRenderedAt;

  return (
    <>
      <div className="workspace-head">
        <div>
          <h2 className="workspace-title">
            Package {packageDraft && <em>v{packageDraft.version} draft</em>}
          </h2>
          <div className="workspace-subtitle">
            {packageDraft
              ? <>Generated {new Date(packageDraft.generatedAt).toLocaleString()} · {packageDraft.sections.length} sections · template <span style={{ color: 'var(--accent)' }}>{packageDraft.template}</span></>
              : 'No package yet — generate one once title is solid'}
          </div>
        </div>
        <div className="workspace-actions">
          <button className="btn-ghost" onClick={onGenerate} disabled={generating}>
            {generating ? 'Generating…' : (packageDraft ? 'Regenerate' : 'Generate')}
          </button>
          {packageDraft && (
            <>
              <button
                className="btn-ghost"
                onClick={onRenderPdf}
                disabled={rendering}
                title="Render the package to PDF (opens in new tab)"
              >
                {rendering ? 'Rendering…' : (pdfReady && !pdfFresh ? 'Re-render PDF' : 'Render PDF')}
              </button>
              {pdfReady && (
                <a
                  className="btn-primary"
                  href={packageApi.pdfUrl(dealId)}
                  target="_blank"
                  rel="noreferrer"
                >Open PDF ↗</a>
              )}
            </>
          )}
        </div>
      </div>

      {genError && (
        <div className="readiness-banner blocked">
          <span className="readiness-icon">!</span>
          <div><strong>Couldn't {generating ? 'generate' : 'render'}:</strong> {genError}</div>
        </div>
      )}

      {/* MAP UPLOAD */}
      <div className="map-upload-section">
        <div className="map-upload-head">
          <div className="map-upload-title">
            Tract map <span className="map-upload-tag">embedded in PDF</span>
          </div>
          <div className="map-upload-actions">
            {mapHasImg && (
              <button className="btn-ghost" onClick={onRemoveMap}>Remove</button>
            )}
            <button className="btn-ghost" onClick={() => mapInputRef.current?.click()} disabled={mapUploading}>
              {mapUploading ? 'Uploading…' : (mapHasImg ? 'Replace' : 'Upload screenshot')}
            </button>
          </div>
        </div>
        <input
          ref={mapInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          style={{ display: 'none' }}
          onChange={e => onUploadMap(e.target.files)}
        />
        {mapHasImg ? (
          <div className="map-preview">
            <img
              src={`${packageApi.mapUrl(dealId)}&v=${mapBust}`}
              alt="Tract map preview"
            />
          </div>
        ) : (
          <div
            className="map-dropzone"
            onClick={() => mapInputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); void onUploadMap(e.dataTransfer.files); }}
          >
            Drop a screenshot here · or click to upload
          </div>
        )}
      </div>

      <div className="package-preview">
        {!packageDraft && !generating && (
          <div className="empty-state" style={{ paddingTop: 50 }}>
            <strong>No package draft yet</strong>
            Click "Generate" once title is solid — Claude will write a buyer-ready Weld DJ Basin package using the chain, math, theory, comps, and tract data on this deal.
          </div>
        )}
        {generating && !packageDraft && (
          <div className="empty-state" style={{ paddingTop: 50 }}>
            <strong>Writing package…</strong>
            Pulling chain, math, theory, comps. Should take 20-40 seconds.
          </div>
        )}
        {packageDraft?.sections.map(section => (
          <div key={section.id} className={`pkg-section ${tweakingId === section.id || section.status === 'editing' ? 'editing' : ''}`}>
            <div className="pkg-section-meta">
              <span className="pkg-section-num">§ {section.ordinal}</span>
              <span className="pkg-section-name">{section.name}</span>
              <span className={`pkg-section-status ${section.status === 'editing' ? 'editing' : ''}`}>
                {section.status === 'editing' ? 'just regenerated' : section.status}
              </span>
            </div>
            <div
              className="pkg-section-body"
              style={{ padding: '4px 0', fontSize: 14, lineHeight: 1.65, fontFamily: 'var(--display)' }}
              dangerouslySetInnerHTML={{ __html: renderInlineMd(section.bodyMd) }}
            />
            <button
              className="pkg-section-tweak"
              onClick={() => {
                const inst = window.prompt(`Tweak "${section.name}" — what should change?`);
                if (inst) void onTweak(section, inst);
              }}
              disabled={tweakingId === section.id}
            >
              <span className="tweak-icon">↳</span>
              <span>{tweakingId === section.id ? 'Regenerating…' : 'Tweak this section'}</span>
            </button>
          </div>
        ))}
      </div>

    </>
  );
}

